import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import {
  SegmentListMultipleItemMembershipOperator,
  SegmentListSingleItemMembershipOperator,
} from "@redotech/redo-model/checkouts/checkout-tree";
import { ProductOrderedSegment } from "@redotech/redo-model/post-purchase/post-purchase-flow";
import { ProductInfo } from "@redotech/shopify-storefront/products";
import {
  convertShopifyIdToGid,
  Gid,
  parseNumericIdFromShopifyId,
  ShopifyIdType,
} from "@redotech/shopify-util/id";
import { memo, useState } from "react";
import { RedoBadge } from "../../../arbiter-components/badge/redo-badge";
import { RedoButton } from "../../../arbiter-components/buttons/redo-button";
import { Flex } from "../../../flex";
import { LoadingRedoAnimation } from "../../../loading-redo-animation";
import {
  ModalProduct,
  ProductSelectionModal,
} from "../../../product/product-selection-modal";
import { Text } from "../../../text";
import { OperatorSelector } from "../../operator-selector";
import * as css from "./products-ordered.module.css";

interface ProductsOrderedSegmentCommonProps {
  condition: ProductOrderedSegment;
  getProductsByGids: (ids: Gid[]) => Promise<ProductInfo[]>;
}

export type ProductsOrderedSegmentProps = ProductsOrderedSegmentCommonProps &
  (
    | {
        readonly: false;
        setCondition: (condition: ProductOrderedSegment) => void;
        searchProducts: (params: { search: string }) => Promise<ModalProduct[]>;
      }
    | { readonly: true }
  );

const segmentListMultipleItemMembershipOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "At least one",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "No",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "Every",
};

const segmentListSingleItemMembershipOperatorToText: Record<
  SegmentListSingleItemMembershipOperator,
  string
> = {
  [SegmentListSingleItemMembershipOperator.IN]: "is",
  [SegmentListSingleItemMembershipOperator.NOT_IN]: "is not",
};

export const ProductsOrderedSegmentCondition = memo(
  function ProductsOrderedSegmentCondition({
    condition,
    getProductsByGids,
    ...props
  }: ProductsOrderedSegmentProps) {
    const [modalOpen, setModalOpen] = useState(false);

    // Load product names for display
    const productNamesLoad = useLoad(async () => {
      if (condition.productIds.length === 0) {
        return {};
      }

      const gids = condition.productIds.map((id) =>
        convertShopifyIdToGid(id, ShopifyIdType.Product),
      );
      const products = await getProductsByGids(gids);
      const nameMap: { [gid: string]: string } = {};
      for (const product of products) {
        nameMap[product.id] = product.title;
      }

      return nameMap;
    }, [condition.productIds, getProductsByGids]);

    const handleCartOperatorSelected = useHandler(
      (cartOperator: SegmentListMultipleItemMembershipOperator) => {
        if (!props.readonly) {
          props.setCondition({ ...condition, cartOperator });
        }
      },
    );

    const handleProductListOperatorSelected = useHandler(
      (productListOperator: SegmentListSingleItemMembershipOperator) => {
        if (!props.readonly) {
          props.setCondition({ ...condition, productListOperator });
        }
      },
    );

    const handleAddProduct = useHandler(() => {
      if (!props.readonly) {
        setModalOpen(true);
      }
    });

    const handleRemoveProduct = useHandler((productId: string) => {
      if (!props.readonly) {
        const updatedProductIds = condition.productIds.filter(
          (id) => id !== productId,
        );
        props.setCondition({ ...condition, productIds: updatedProductIds });
      }
    });

    // Show loading animation while product names are being fetched
    if (condition.productIds.length > 0 && !productNamesLoad.value) {
      return (
        <Flex align="center" dir="row" justify="center" p="md" w="full">
          <LoadingRedoAnimation className={css.smallSpinner} />
        </Flex>
      );
    }

    return (
      <>
        <OperatorSelector<SegmentListMultipleItemMembershipOperator>
          availableOptions={Object.values(
            SegmentListMultipleItemMembershipOperator,
          )}
          onOperatorSelected={handleCartOperatorSelected}
          operatorToText={segmentListMultipleItemMembershipOperatorToText}
          readonly={props.readonly}
          selectedOperator={condition.cartOperator}
        />
        <Text fontSize="xs" textColor="primary">
          product in the cart
        </Text>
        <OperatorSelector<SegmentListSingleItemMembershipOperator>
          availableOptions={Object.values(
            SegmentListSingleItemMembershipOperator,
          )}
          onOperatorSelected={handleProductListOperatorSelected}
          operatorToText={segmentListSingleItemMembershipOperatorToText}
          readonly={props.readonly}
          selectedOperator={condition.productListOperator}
        />
        <Text fontSize="xs" textColor="primary">
          in this list:
        </Text>
        <Flex dir="row" gap="md" w="full" wrap="wrap">
          {condition.productIds.length === 0 ? (
            <Text fontSize="xs" textColor="tertiary">
              No products selected
            </Text>
          ) : (
            condition.productIds.map((productId, index) => {
              const productName =
                productNamesLoad.value?.[productId] ||
                parseNumericIdFromShopifyId(productId);

              return (
                <RedoBadge
                  color="gray"
                  iconTrailing={
                    props.readonly
                      ? undefined
                      : {
                          type: "icon",
                          onClick: () => handleRemoveProduct(productId),
                          Icon: TrashIcon,
                        }
                  }
                  key={index}
                  size="sm"
                  text={productName}
                />
              );
            })
          )}
        </Flex>
        {!props.readonly && (
          <RedoButton
            hierarchy="secondary"
            onClick={handleAddProduct}
            size="xs"
            text="Add product"
          />
        )}
        {!props.readonly && (
          <ProductSelectionModal
            multiselect
            open={modalOpen}
            searchProducts={props.searchProducts}
            setOpen={setModalOpen}
            setSelected={(selected) => {
              if (selected) {
                const newProductIds = selected.map((s) => s.product.id);
                const uniqueProductIds = [
                  ...new Set([...condition.productIds, ...newProductIds]),
                ];
                props.setCondition({
                  ...condition,
                  productIds: uniqueProductIds,
                });
              }
            }}
          />
        )}
      </>
    );
  },
);
